import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@revelate/types";
import { postAuthenticatedAPI } from "@/lib/api";
import { supabaseClient } from "@/lib/supabase/client";
import type {
  RevenueIntelligenceChatDataset,
} from "../components/RevenueIntelligenceChatContext";
import type {
  RevenueIntelligenceSemanticPlan,
} from "./revenue-intelligence-semantic-planner";

type Json =
  | boolean
  | number
  | string
  | null
  | Json[]
  | { [key: string]: Json | undefined };

export type SavedAnalysisRow = {
  company_id: number;
  contract_versions: Json;
  created_at: string;
  id: string;
  latest_run_id: string | null;
  name: string;
  original_instruction: string;
  owner_user_id: string;
  period_behavior: "relative" | "fixed" | "ask";
  recipe: Json;
  updated_at: string;
  visibility: "private" | "company";
};

type SavedAnalysisInsert = Omit<
  SavedAnalysisRow,
  "created_at" | "id" | "latest_run_id" | "updated_at"
> & {
  id?: string;
  latest_run_id?: string | null;
};

export type AnalysisRunRow = {
  company_id: number;
  completed_at: string | null;
  contract_versions: Json;
  error_code: string | null;
  id: string;
  owner_user_id: string;
  recipe: Json;
  resolved_period: Json;
  result: Json | null;
  saved_analysis_id: string | null;
  started_at: string;
  status: "running" | "completed" | "failed";
};

type AnalysisRunInsert = Omit<AnalysisRunRow, "id" | "started_at"> & {
  id?: string;
  started_at?: string;
};

type AnalysisSnapshotRow = {
  analysis_run_id: string | null;
  brief_id: string | null;
  company_id: number;
  created_at: string;
  id: string;
  name: string;
  owner_user_id: string;
  snapshot: Json;
};

type AnalysisSnapshotInsert = Omit<AnalysisSnapshotRow, "created_at" | "id"> & {
  id?: string;
};

export type BriefRow = {
  company_id: number;
  created_at: string;
  id: string;
  name: string;
  owner_user_id: string;
  period_behavior: "relative" | "fixed" | "ask";
  presentation_preferences: Json;
  updated_at: string;
  visibility: "private" | "company";
};

type BriefInsert = Omit<BriefRow, "created_at" | "id" | "updated_at"> & {
  id?: string;
};

type BriefItemRow = {
  brief_id: string;
  company_id: number;
  created_at: string;
  id: string;
  owner_user_id: string;
  position: number;
  saved_analysis_id: string;
};

type BriefItemInsert = Omit<BriefItemRow, "created_at" | "id"> & {
  id?: string;
};

type ChatDatabase = {
  __InternalSupabase: Database["__InternalSupabase"];
  public: {
    Tables: Database["public"]["Tables"] & {
      saved_analyses: {
        Row: SavedAnalysisRow;
        Insert: SavedAnalysisInsert;
        Update: Partial<SavedAnalysisInsert>;
        Relationships: [];
      };
      analysis_runs: {
        Row: AnalysisRunRow;
        Insert: AnalysisRunInsert;
        Update: Partial<AnalysisRunInsert>;
        Relationships: [];
      };
      analysis_snapshots: {
        Row: AnalysisSnapshotRow;
        Insert: AnalysisSnapshotInsert;
        Update: never;
        Relationships: [];
      };
      briefs: {
        Row: BriefRow;
        Insert: BriefInsert;
        Update: Partial<BriefInsert>;
        Relationships: [];
      };
      brief_items: {
        Row: BriefItemRow;
        Insert: BriefItemInsert;
        Update: Partial<BriefItemInsert>;
        Relationships: [];
      };
    };
    Views: Database["public"]["Views"];
    Functions: Database["public"]["Functions"];
    Enums: Database["public"]["Enums"];
    CompositeTypes: Database["public"]["CompositeTypes"];
  };
};

const chatClient = supabaseClient as unknown as SupabaseClient<ChatDatabase>;

function canUseLocalMemoryWorkspace() {
  if (!import.meta.env.DEV || typeof window === "undefined") return false;
  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

async function requestLocalMemoryWorkspace<T>({
  companyId,
  operation,
  payload,
}: {
  companyId: number;
  operation:
    | "list_saved_analyses"
    | "get_analysis_run"
    | "save_analysis"
    | "record_refresh"
    | "create_brief"
    | "save_brief_snapshot";
  payload?: Record<string, unknown>;
}) {
  if (!canUseLocalMemoryWorkspace()) {
    throw new Error("The local analysis workspace is unavailable.");
  }
  const { data } = await supabaseClient.auth.getSession();
  const accessToken = data.session?.access_token;
  if (!accessToken) throw new Error("Sign in to use the analysis workspace.");
  const response = await postAuthenticatedAPI<{
    data: T;
    mode: "local_memory";
    requestId: string;
  }>(
    "/revenue-intelligence/chat/workspace",
    {
      companyId: String(companyId),
      operation,
      payload,
    },
    accessToken,
  );
  return response.data;
}

export type SavedAnalysisRecipe = {
  version: "1";
  conclusion: string | null;
  contractId: string;
  datasetId: string;
  instruction: string;
  periodLabel: string | null;
  plan: RevenueIntelligenceSemanticPlan;
  route: string;
  recordIds: string[];
  scopeActionIds: string[];
};

function asJson(value: unknown) {
  return value as Json;
}

export async function listRevenueIntelligenceSavedAnalyses(
  companyId: number,
) {
  const { data, error } = await chatClient
    .from("saved_analyses")
    .select("*")
    .eq("company_id", companyId)
    .order("updated_at", { ascending: false })
    .limit(12);
  if (error) {
    return requestLocalMemoryWorkspace<SavedAnalysisRow[]>({
      companyId,
      operation: "list_saved_analyses",
    });
  }
  return data;
}

export async function saveRevenueIntelligenceAnalysis({
  companyId,
  conclusion,
  dataset,
  instruction,
  name,
  periodBehavior,
  periodLabel,
  plan,
  route,
  userId,
  recordIds = [],
  scopeActionIds = [],
}: {
  companyId: number;
  conclusion?: string | null;
  dataset: RevenueIntelligenceChatDataset;
  instruction: string;
  name: string;
  periodBehavior: "relative" | "fixed" | "ask";
  periodLabel?: string | null;
  plan: RevenueIntelligenceSemanticPlan;
  route: string;
  userId: string;
  recordIds?: string[];
  scopeActionIds?: string[];
}) {
  if (!plan.contractId || (!plan.datasetId && !plan.recordSetId)) {
    throw new Error("The analysis recipe is incomplete.");
  }
  const recipe: SavedAnalysisRecipe = {
    version: "1",
    conclusion: conclusion ?? null,
    contractId: plan.contractId,
    datasetId: plan.datasetId ?? dataset.datasetId,
    instruction,
    periodLabel: periodLabel ?? null,
    plan,
    route,
    recordIds: [...new Set(recordIds)].slice(0, 50),
    scopeActionIds: [...new Set(scopeActionIds)],
  };
  const contractVersions = {
    [plan.contractId]: "1",
  };
  try {
    const { data: savedAnalysis, error: saveError } = await chatClient
      .from("saved_analyses")
      .insert({
        company_id: companyId,
        owner_user_id: userId,
        name,
        original_instruction: instruction,
        recipe: asJson(recipe),
        contract_versions: asJson(contractVersions),
        period_behavior: periodBehavior,
        visibility: "private",
      })
      .select("*")
      .single();
    if (saveError) throw saveError;

    const { data: run, error: runError } = await chatClient
      .from("analysis_runs")
      .insert({
        company_id: companyId,
        owner_user_id: userId,
        saved_analysis_id: savedAnalysis.id,
        status: "completed",
        resolved_period: asJson({ label: periodLabel ?? null }),
        recipe: asJson(recipe),
        contract_versions: asJson(contractVersions),
        result: asJson({ dataset, plan }),
        error_code: null,
        completed_at: new Date().toISOString(),
      })
      .select("*")
      .single();
    if (runError) throw runError;

    const { error: updateError } = await chatClient
      .from("saved_analyses")
      .update({ latest_run_id: run.id })
      .eq("id", savedAnalysis.id);
    if (updateError) throw updateError;
    return { ...savedAnalysis, latest_run_id: run.id };
  } catch {
    return requestLocalMemoryWorkspace<SavedAnalysisRow>({
      companyId,
      operation: "save_analysis",
      payload: {
        contractVersions,
        name,
        originalInstruction: instruction,
        periodBehavior,
        recipe,
        resolvedPeriod: { label: periodLabel ?? null },
        result: { dataset, plan },
      },
    });
  }
}

export async function getRevenueIntelligenceAnalysisRun({
  companyId,
  runId,
}: {
  companyId: number;
  runId: string;
}) {
  const { data, error } = await chatClient
    .from("analysis_runs")
    .select("*")
    .eq("company_id", companyId)
    .eq("id", runId)
    .single();
  if (error) {
    return requestLocalMemoryWorkspace<AnalysisRunRow>({
      companyId,
      operation: "get_analysis_run",
      payload: { runId },
    });
  }
  return data;
}

export function parseAnalysisRunResult(value: Json | null) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  if (!candidate.dataset || !candidate.plan) return null;
  return candidate as unknown as {
    dataset: RevenueIntelligenceChatDataset;
    plan: RevenueIntelligenceSemanticPlan;
  };
}

export async function recordRevenueIntelligenceAnalysisRefresh({
  companyId,
  dataset,
  recipe,
  savedAnalysisId,
  userId,
}: {
  companyId: number;
  dataset: RevenueIntelligenceChatDataset;
  recipe: SavedAnalysisRecipe;
  savedAnalysisId: string;
  userId: string;
}) {
  const { data: run, error } = await chatClient
    .from("analysis_runs")
    .insert({
      company_id: companyId,
      owner_user_id: userId,
      saved_analysis_id: savedAnalysisId,
      status: "completed",
      resolved_period: asJson({ label: recipe.periodLabel }),
      recipe: asJson(recipe),
      contract_versions: asJson({ [recipe.contractId]: "1" }),
      result: asJson({ dataset, plan: recipe.plan }),
      error_code: null,
      completed_at: new Date().toISOString(),
    })
    .select("*")
    .single();
  if (error) {
    return requestLocalMemoryWorkspace<{ id: string }>({
      companyId,
      operation: "record_refresh",
      payload: {
        contractVersions: { [recipe.contractId]: "1" },
        recipe,
        resolvedPeriod: { label: recipe.periodLabel },
        result: { dataset, plan: recipe.plan },
        savedAnalysisId,
      },
    });
  }

  const { error: updateError } = await chatClient
    .from("saved_analyses")
    .update({ latest_run_id: run.id })
    .eq("id", savedAnalysisId);
  if (updateError) throw updateError;
  return run;
}

export async function createRevenueIntelligenceBrief({
  companyId,
  name,
  savedAnalysisIds,
  userId,
}: {
  companyId: number;
  name: string;
  savedAnalysisIds: string[];
  userId: string;
}) {
  const selectedIds = [...new Set(savedAnalysisIds)].slice(0, 3);
  if (!selectedIds.length) throw new Error("Select at least one Saved analysis.");

  const { data: brief, error } = await chatClient
    .from("briefs")
    .insert({
      company_id: companyId,
      owner_user_id: userId,
      name,
      period_behavior: "relative",
      presentation_preferences: asJson({
        format: "16:9",
        signature: "Intelligence by Revelate",
      }),
      visibility: "private",
    })
    .select("*")
    .single();
  if (error) {
    return requestLocalMemoryWorkspace<BriefRow>({
      companyId,
      operation: "create_brief",
      payload: {
        name,
        presentationPreferences: {
          format: "16:9",
          signature: "Intelligence by Revelate",
        },
        savedAnalysisIds: selectedIds,
      },
    });
  }

  const { error: itemsError } = await chatClient
    .from("brief_items")
    .insert(selectedIds.map((savedAnalysisId, position) => ({
      brief_id: brief.id,
      company_id: companyId,
      owner_user_id: userId,
      position,
      saved_analysis_id: savedAnalysisId,
    })));
  if (itemsError) throw itemsError;
  return brief;
}

export async function saveRevenueIntelligenceBriefSnapshot({
  briefId,
  companyId,
  name,
  snapshot,
  userId,
}: {
  briefId: string;
  companyId: number;
  name: string;
  snapshot: unknown;
  userId: string;
}) {
  const { data, error } = await chatClient
    .from("analysis_snapshots")
    .insert({
      analysis_run_id: null,
      brief_id: briefId,
      company_id: companyId,
      owner_user_id: userId,
      name,
      snapshot: asJson(snapshot),
    })
    .select("*")
    .single();
  if (error) {
    return requestLocalMemoryWorkspace<AnalysisSnapshotRow>({
      companyId,
      operation: "save_brief_snapshot",
      payload: {
        briefId,
        name,
        snapshot,
      },
    });
  }
  return data;
}

export function parseSavedAnalysisRecipe(value: Json) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  if (
    candidate.version !== "1"
    || typeof candidate.contractId !== "string"
    || typeof candidate.datasetId !== "string"
    || typeof candidate.instruction !== "string"
    || typeof candidate.route !== "string"
    || !candidate.plan
  ) {
    return null;
  }
  return {
    ...(candidate as unknown as SavedAnalysisRecipe),
    recordIds: Array.isArray(candidate.recordIds)
      ? candidate.recordIds.filter((value): value is string => typeof value === "string")
      : [],
    scopeActionIds: Array.isArray(candidate.scopeActionIds)
      ? candidate.scopeActionIds.filter((value): value is string => typeof value === "string")
      : [],
  };
}
